// src/utils/userManagementApi.js
import config from "../config";
import logger from "./logger";

/**
 * Create a new user
 */
export const createUser = async (userData, getAccessToken) => {
  const accessToken = await getAccessToken();

  const response = await fetch(`${config.servicesApiEndpoint}/users`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "x-api-key": config.apiKey,
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(userData),
  });

  if (!response.ok) {
    const error = await response.json();
    const errorMessage = error.message || "Failed to create user";
    logger.error("Failed to create user", new Error(errorMessage));
    throw new Error(errorMessage);
  }

  return await response.json();
};

/**
 * List users with optional search, filter, and pagination
 */
export const listUsers = async (params, getAccessToken) => {
  logger.log("[UserManagementAPI] listUsers called with params:", params);

  const accessToken = await getAccessToken();
  logger.log(
    "[UserManagementAPI] Got access token:",
    accessToken ? "Token exists" : "No token"
  );

  const queryParams = new URLSearchParams();
  if (params.search) queryParams.append("search", params.search);
  if (params.role) queryParams.append("role", params.role);
  if (params.page) queryParams.append("page", params.page.toString());
  if (params.limit) queryParams.append("limit", params.limit.toString());

  const url = `${config.servicesApiEndpoint}/users?${queryParams.toString()}`;
  logger.log("[UserManagementAPI] Request URL:", url);
  logger.log("[UserManagementAPI] API Endpoint:", config.servicesApiEndpoint);
  logger.log(
    "[UserManagementAPI] API Key:",
    config.apiKey ? "API Key exists" : "No API Key"
  );

  const response = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      "x-api-key": config.apiKey,
      Authorization: `Bearer ${accessToken}`,
    },
  });

  logger.log("[UserManagementAPI] Response status:", response.status);
  logger.log(
    "[UserManagementAPI] Response headers:",
    Object.fromEntries(response.headers.entries())
  );

  if (!response.ok) {
    const errorText = await response.text();
    logger.error("[UserManagementAPI] Error response body:", errorText);
    let error;
    try {
      error = JSON.parse(errorText);
    } catch {
      error = { message: errorText };
    }
    throw new Error(error.message || "Failed to list users");
  }

  const data = await response.json();
  logger.log("[UserManagementAPI] Success response:", data);
  logger.log(
    "[UserManagementAPI] *** RAW RESPONSE BODY ***:",
    JSON.stringify(data, null, 2)
  );
  logger.log("[UserManagementAPI] First user raw:", data.users?.[0]);
  logger.log(
    "[UserManagementAPI] First user keys:",
    data.users?.[0] ? Object.keys(data.users[0]) : "No users"
  );
  return data;
};

/**
 * Update user profile and/or role
 */
export const updateUser = async (userId, updates, getAccessToken) => {
  const accessToken = await getAccessToken();

  const response = await fetch(
    `${config.servicesApiEndpoint}/users/${encodeURIComponent(userId)}`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        "x-api-key": config.apiKey,
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(updates),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || "Failed to update user");
  }

  return await response.json();
};

/**
 * Re-enable an unusable user account
 */
export const reenableUser = async (userId, getAccessToken) => {
  const accessToken = await getAccessToken();

  const response = await fetch(
    `${config.servicesApiEndpoint}/users/${encodeURIComponent(userId)}`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        "x-api-key": config.apiKey,
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ unusableAccount: false }),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || "Failed to re-enable user");
  }

  return await response.json();
};

/**
 * Delete a user
 */
export const deleteUser = async (userId, getAccessToken) => {
  const accessToken = await getAccessToken();

  const response = await fetch(
    `${config.servicesApiEndpoint}/users/${encodeURIComponent(userId)}`,
    {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        "x-api-key": config.apiKey,
        Authorization: `Bearer ${accessToken}`,
      },
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || "Failed to delete user");
  }

  return await response.json();
};

/**
 * Get user's organizations
 */
export const getUserOrganizations = async (userId, getAccessToken) => {
  const accessToken = await getAccessToken();

  const response = await fetch(
    `${config.servicesApiEndpoint}/users/${encodeURIComponent(userId)}/organizations`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "x-api-key": config.apiKey,
        Authorization: `Bearer ${accessToken}`,
      },
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || "Failed to get user organizations");
  }

  return await response.json();
};

/**
 * Grant user access to an organization
 */
export const grantUserOrgAccess = async (userId, orgId, getAccessToken) => {
  const accessToken = await getAccessToken();

  const response = await fetch(
    `${config.servicesApiEndpoint}/users/${encodeURIComponent(userId)}/organizations`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-api-key": config.apiKey,
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ orgId }),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    const errorMessage = error.message || "Failed to grant organization access";
    logger.error(
      "Failed to grant organization access",
      new Error(errorMessage)
    );
    throw new Error(errorMessage);
  }

  return await response.json();
};

/**
 * Get all users authorized to an organization
 */
export const getOrganizationUsers = async (orgId, getAccessToken) => {
  const accessToken = await getAccessToken();

  const response = await fetch(
    `${config.authorizationApiEndpoint}/organizations/${encodeURIComponent(
      orgId
    )}/users`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "x-api-key": config.apiKey,
        Authorization: `Bearer ${accessToken}`,
      },
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || "Failed to get organization users");
  }

  return await response.json();
};

/**
 * Revoke user access from an organization
 */
export const revokeUserOrgAccess = async (userId, orgId, getAccessToken) => {
  const accessToken = await getAccessToken();

  const response = await fetch(
    `${config.servicesApiEndpoint}/users/${encodeURIComponent(
      userId
    )}/organizations/${encodeURIComponent(orgId)}`,
    {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        "x-api-key": config.apiKey,
        Authorization: `Bearer ${accessToken}`,
      },
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || "Failed to revoke organization access");
  }

  return await response.json();
};

/**
 * Ensure user has federated ID set (creates it if missing)
 */
export const ensureUserFederatedId = async (userId, getAccessToken) => {
  const accessToken = await getAccessToken();

  const response = await fetch(
    `${config.servicesApiEndpoint}/users/${encodeURIComponent(
      userId
    )}/ensure-federated-id`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-api-key": config.apiKey,
        Authorization: `Bearer ${accessToken}`,
      },
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || "Failed to ensure federated ID");
  }

  return await response.json();
};
